import React, { useState, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import StockChart from '../components/Charts/StockChart';
import ChartSettings from '../components/Charts/ChartSettings';
import TrendLineManager from '../components/Charts/TrendLineManager';
import { stockSearchService } from '../config/api';

const Charts = () => {
  const [chartSettings, setChartSettings] = useState({
    symbol: 'TITAN',
    interval: '1h',
    theme: 'light'
  });
  const [trendLines, setTrendLines] = useState([]);

  // Stock search state
  const [stockOptions, setStockOptions] = useState([]);
  const [stockSearchLoading, setStockSearchLoading] = useState(false);
  const [stockInputValue, setStockInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const handleSettingsChange = (newSettings) => {
    setChartSettings(newSettings);
  };

  // Handle stock search
  const handleStockSearch = async (query) => {
    if (!query || query.length < 2) {
      setStockOptions([]);
      setShowDropdown(false);
      return;
    }

    try {
      setStockSearchLoading(true);
      const response = await stockSearchService.search(query);
      const stocks = response.data || [];
      setStockOptions(stocks.slice(0, 10));
      setShowDropdown(stocks.length > 0);
    } catch (error) {
      console.error('Error searching stocks:', error);
      setStockOptions([]);
      setShowDropdown(false);
    } finally {
      setStockSearchLoading(false);
    }
  };

  // Handle stock selection
  const handleStockSelect = (stock) => {
    const symbol = typeof stock === 'string' ? stock : stock.tradingSymbol;
    setChartSettings(prev => ({ ...prev, symbol }));
    setStockInputValue(symbol);
    setShowDropdown(false);
  };

  // Handle trend lines change from StockChart (KLineCharts)
  const handleTrendLinesChange = useCallback((lines) => {
    console.log('[Charts] Trend lines updated:', lines);
    setTrendLines(lines);
  }, []);

  // Handle trend line deletion
  const handleDeleteTrendLine = useCallback((lineId) => {
    setTrendLines(prev => prev.filter(line => line.id !== lineId));
  }, []);

  return (
    <DashboardLayout>
      <div className="p-4 h-full">
        <div className="card bg-base-100 shadow-lg w-full h-full overflow-auto">
          <div className="card-body">
            <h2 className="card-title text-2xl text-base-content mb-4">
              Stock Charts - Real-Time Data
            </h2>

            {/* Controls Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mb-6">
              {/* Stock Search */}
              <div className="fieldset w-full relative">
                <label className="label">
                  <span className="text-sm">Search Stock</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Type stock name (e.g., TITAN, RELIANCE)"
                    className="input input w-full"
                    value={stockInputValue}
                    onChange={(e) => {
                      setStockInputValue(e.target.value);
                      handleStockSearch(e.target.value);
                    }}
                    onFocus={() => stockOptions.length > 0 && setShowDropdown(true)}
                  />
                  {stockSearchLoading && (
                    <span className="loading loading-spinner loading-sm absolute right-3 top-3"></span>
                  )}
                </div>
                {/* Dropdown */}
                {showDropdown && stockOptions.length > 0 && (
                  <ul className="menu bg-base-200 rounded-box absolute z-50 w-full mt-1 shadow-lg max-h-60 overflow-auto top-full">
                    {stockOptions.map((option) => (
                      <li key={option.id || option.tradingSymbol}>
                        <button
                          className="flex flex-col items-start"
                          onClick={() => handleStockSelect(option)}
                        >
                          <span className="font-bold text-base-content">{option.tradingSymbol}</span>
                          <span className="text-xs text-base-content/50">{option.name} • {option.exchange}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Chart Settings */}
              <div>
                <ChartSettings
                  settings={chartSettings}
                  onSettingsChange={handleSettingsChange}
                />
              </div>

              {/* Current Info */}
              <div className="text-base-content/70">
                Current: <strong className="text-base-content">{chartSettings.symbol}</strong> |
                Interval: <strong className="text-base-content">{chartSettings.interval}</strong>
              </div>
            </div>

            <div className="divider"></div>

            {/* Charts Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              {/* Main Chart */}
              <div className="lg:col-span-9">
                <div className="card bg-base-100 shadow">
                  <div className="card-body p-4">
                    <h3 className="font-semibold text-base-content mb-2">
                      Interactive Chart (Draw Trend Lines Here)
                    </h3>
                    <div style={{ minHeight: '500px' }}>
                      <StockChart
                        key={`kline-${chartSettings.symbol}-${chartSettings.interval}-${chartSettings.theme}`}
                        symbol={chartSettings.symbol}
                        interval={chartSettings.interval}
                        theme={chartSettings.theme}
                        height="100%"
                        onTrendLinesChange={handleTrendLinesChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar - Trend Line Manager */}
              <div className="lg:col-span-3">
                <TrendLineManager
                  trendLines={trendLines}
                  onDeleteTrendLine={handleDeleteTrendLine}
                  symbol={chartSettings.symbol}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Charts;
